import React from 'react';

interface State {
  hasError: boolean;
}

const CHUNK_RELOAD_KEY = 'chunk_error_reloaded';

const isChunkLoadError = (error: Error) =>
  error.message?.includes('Loading CSS chunk') || error.message?.includes('Loading chunk');

export class ErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    // Don't show error UI for chunk errors — componentDidCatch will reload the page
    return { hasError: !isChunkLoadError(error) };
  }

  componentDidMount() {
    // Clear the reload guard after a successful load so future chunk errors can retry
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isChunkLoadError(error) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
      return;
    }

    try {
      fetch('/api/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error: error.message,
          componentStack: info.componentStack,
          url: window.location.href,
        }),
      });
    } catch {
      // Silently ignore if reporting fails
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Something went wrong.</h2>
          <button onClick={() => window.location.reload()}>Reload page</button>
        </div>
      );
    }
    return this.props.children;
  }
}
